import { useEffect, useMemo, useState } from "react"
import { Box, HStack, Input, Stack } from "@chakra-ui/react"
import { FaChevronDown, FaChevronRight } from "react-icons/fa"
import { Button, Text } from "@/components"
import { DealPunchlist as DealPunchlistType } from "@/types"

interface Props {
  punchlist: DealPunchlistType | null
  onUpdate?: (field: keyof DealPunchlistType, value: boolean | number | null) => void
}

type StageType = "dueDiligence" | "offer" | "contract" | "backgroundCheck" | "preClose" | "closing" | "postClosing"

interface FieldConfig {
  key: keyof DealPunchlistType
  label: string
  type: "boolean" | "number"
}

const STAGE_CONFIG: Record<StageType, { label: string; color: string; bgColor: string; borderColor: string }> = {
  dueDiligence: { label: "Stage 1 — Due Diligence", color: "blue.700", bgColor: "blue.50", borderColor: "blue.200" },
  offer: { label: "Stage 2 — Offer", color: "teal.700", bgColor: "teal.50", borderColor: "teal.200" },
  contract: { label: "Stage 3 — Contract", color: "purple.700", bgColor: "purple.50", borderColor: "purple.200" },
  backgroundCheck: { label: "Stage 4 — Background Check", color: "orange.700", bgColor: "orange.50", borderColor: "orange.200" },
  preClose: { label: "Stage 5 — Pre-Close", color: "green.700", bgColor: "green.50", borderColor: "green.200" },
  closing: { label: "Stage 6 — Closing", color: "red.700", bgColor: "red.50", borderColor: "red.200" },
  postClosing: { label: "Stage 7 — Post-Closing", color: "cyan.700", bgColor: "cyan.50", borderColor: "cyan.200" },
}

const STAGE_FIELDS: Record<StageType, FieldConfig[]> = {
  dueDiligence: [
    { key: "isSignedNda", label: "Signed NDA", type: "boolean" },
    { key: "isLatestCoi", label: "Latest COI", type: "boolean" },
    { key: "isCapTableCreated", label: "Cap Table Created", type: "boolean" },
    { key: "isFmvConfirmed", label: "FMV Confirmed", type: "boolean" },
    { key: "isGrantAgreement", label: "Grant Agreement Obtained", type: "boolean" },
    { key: "isGrantAgreementReviewed", label: "Grant Agreement Reviewed", type: "boolean" },
    { key: "isOptionPlan", label: "Option Plan Obtained", type: "boolean" },
    { key: "isOptionPlanReviewed", label: "Option Plan Reviewed", type: "boolean" },
  ],
  offer: [
    { key: "isOfferSent", label: "Offer Sent", type: "boolean" },
    { key: "isVerballyAccepted", label: "Verbally Accepted", type: "boolean" },
    { key: "isClosingInstructionsSent", label: "Closing Instructions Sent", type: "boolean" },
  ],
  contract: [
    { key: "isContractGenerated", label: "Contract Generated", type: "boolean" },
    { key: "isContractSigned", label: "Contract Signed", type: "boolean" },
    { key: "isLimitedPowerAttorneySigned", label: "LPOA Signed", type: "boolean" },
    { key: "isTaxPrimerSent", label: "Tax Primer Sent", type: "boolean" },
  ],
  backgroundCheck: [
    { key: "isBackgroundCheckSigned", label: "Background Check Auth Signed", type: "boolean" },
    { key: "isBackgroundClean", label: "Background Report Clean", type: "boolean" },
    { key: "isPhotoIdBackgroundMatch", label: "Photo ID Obtained", type: "boolean" },
    { key: "isIdConfirmed", label: "ID Confirmed (Face Match)", type: "boolean" },
  ],
  preClose: [
    { key: "isLinkedInSaved", label: "LinkedIn Saved", type: "boolean" },
    { key: "isOwnershipConfirmed", label: "Vesting / Ownership Confirmed", type: "boolean" },
    { key: "isOtherAgreementsCleared", label: "Other Agreements Cleared (ROFR, Co-Sale)", type: "boolean" },
    { key: "isPitchbookReviewed", label: "PitchBook Reviewed", type: "boolean" },
    { key: "isExerciseFormReviewed", label: "Exercise Form Reviewed", type: "boolean" },
    { key: "isNsoAccurate", label: "NSO Tax Recalculated / Accurate", type: "boolean" },
    { key: "isGoogleAlerts", label: "Google Alerts Set", type: "boolean" },
    { key: "isClosingSessionReviewed", label: "Closing Script Reviewed", type: "boolean" },
    { key: "isSignedOff", label: "Backup Manager Sign-Off", type: "boolean" },
    { key: "creditScore", label: "Credit Score", type: "number" },
  ],
  closing: [
    { key: "isExerciseConfirmed", label: "Exercise Confirmed", type: "boolean" },
    { key: "isStockCertificate", label: "Stock Certificate Photographed", type: "boolean" },
  ],
  postClosing: [
    { key: "isContractFullyExecuted", label: "Contract Fully Executed", type: "boolean" },
    { key: "isStockCertificateReceived", label: "Stock Certificate Received", type: "boolean" },
    { key: "isInSharepoint", label: "Docs Uploaded to SharePoint", type: "boolean" },
    { key: "isIrrUpdated", label: "IRR Updated", type: "boolean" },
    { key: "isAdditionalPaymentSent", label: "Additional Payment Sent", type: "boolean" },
    { key: "isAdditionalPaymentReceived", label: "Additional Payment Received", type: "boolean" },
  ],
}

const ALL_STAGES: StageType[] = ["dueDiligence", "offer", "contract", "backgroundCheck", "preClose", "closing", "postClosing"]

const BOOLEAN_FIELD_KEYS = Object.values(STAGE_FIELDS)
  .flat()
  .filter((f) => f.type === "boolean")
  .map((f) => f.key)

export const DealPunchlist = ({ punchlist, onUpdate }: Props) => {
  const [expandedSections, setExpandedSections] = useState<Set<StageType>>(new Set<StageType>([]))
  const [localData, setLocalData] = useState<Record<string, boolean | number | null>>({})

  // Sync local state from punchlist prop
  useEffect(() => {
    const data: Record<string, boolean | number | null> = {}
    Object.values(STAGE_FIELDS)
      .flat()
      .forEach((field) => {
        if (punchlist) {
          data[field.key] = punchlist[field.key] as boolean | number | null
        } else {
          data[field.key] = field.type === "boolean" ? false : null
        }
      })
    setLocalData(data)
  }, [punchlist])

  const totalChecked = useMemo(() => {
    return BOOLEAN_FIELD_KEYS.filter((key) => localData[key] === true).length
  }, [localData])

  const toggleSection = (stage: StageType) => {
    const next = new Set(expandedSections)
    if (next.has(stage)) {
      next.delete(stage)
    } else {
      next.add(stage)
    }
    setExpandedSections(next)
  }

  const expandAll = () => setExpandedSections(new Set(ALL_STAGES))
  const collapseAll = () => setExpandedSections(new Set())

  const handleFieldChange = (field: keyof DealPunchlistType, value: boolean | number | null) => {
    setLocalData((prev) => ({ ...prev, [field]: value }))
    if (onUpdate) {
      onUpdate(field, value)
    }
  }

  const getStageCheckedCount = (stage: StageType) => {
    const fields = STAGE_FIELDS[stage]
    const booleanFields = fields.filter((f) => f.type === "boolean")
    return booleanFields.filter((f) => localData[f.key] === true).length
  }

  const getStageTotalCount = (stage: StageType) => {
    return STAGE_FIELDS[stage].filter((f) => f.type === "boolean").length
  }

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <HStack gap={3}>
          <Text fontSize="lg" fontWeight="bold">Closing Punchlist</Text>
          <Text fontSize="sm" color="text.muted">
            {totalChecked}/{BOOLEAN_FIELD_KEYS.length} complete
          </Text>
        </HStack>
        <Button
          size="xs"
          variant="outline"
          onClick={expandedSections.size === ALL_STAGES.length ? collapseAll : expandAll}
        >
          {expandedSections.size === ALL_STAGES.length ? "Collapse All" : "Expand All"}
        </Button>
      </HStack>

      {/* Progress bar */}
      <Box mb={4} h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          bg={totalChecked === BOOLEAN_FIELD_KEYS.length ? "green.500" : "blue.500"}
          borderRadius="full"
          transition="width 0.3s"
          style={{ width: `${(totalChecked / BOOLEAN_FIELD_KEYS.length) * 100}%` }}
        />
      </Box>

      <Stack gap={2}>
        {ALL_STAGES.map((stage) => {
          const config = STAGE_CONFIG[stage]
          const fields = STAGE_FIELDS[stage]
          const isExpanded = expandedSections.has(stage)
          const checked = getStageCheckedCount(stage)
          const total = getStageTotalCount(stage)
          const stageComplete = checked === total

          return (
            <Box key={stage} border="1px" borderColor={config.borderColor} borderRadius={8} overflow="hidden">
              {/* Header */}
              <HStack
                px={2}
                py={1.5}
                bg={config.bgColor}
                cursor="pointer"
                onClick={() => toggleSection(stage)}
                justifyContent="space-between"
                _hover={{ opacity: 0.9 }}
              >
                <HStack gap={2}>
                  {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
                  <Text fontSize="xs" fontWeight="bold" color={config.color}>
                    {config.label}
                  </Text>
                </HStack>
                <Text fontSize="xs" color={stageComplete ? "green.600" : "text.muted"} fontWeight={stageComplete ? "bold" : "normal"}>
                  {stageComplete ? "Done" : `${checked}/${total}`}
                </Text>
              </HStack>

              {/* Field list */}
              {isExpanded && (
                <Stack gap={0} bg="card.bg">
                  {fields.map((field) => {
                    const value = localData[field.key]

                    return (
                      <HStack
                        key={field.key}
                        px={3}
                        py={2}
                        justifyContent="space-between"
                        borderBottom="1px"
                        borderColor="border.default"
                        _last={{ borderBottom: "none" }}
                      >
                        <Text fontSize="sm" color={field.type === "boolean" && value === true ? "text.default" : "text.muted"}>
                          {field.label}
                        </Text>
                        {field.type === "boolean" ? (
                          <HStack gap={1}>
                            <Button
                              size="2xs"
                              variant={value === true ? "solid" : "outline"}
                              colorPalette={value === true ? "green" : "gray"}
                              onClick={() => handleFieldChange(field.key, true)}
                              px={2}
                            >
                              Yes
                            </Button>
                            <Button
                              size="2xs"
                              variant={value === false ? "solid" : "outline"}
                              colorPalette={value === false ? "red" : "gray"}
                              onClick={() => handleFieldChange(field.key, false)}
                              px={2}
                            >
                              No
                            </Button>
                          </HStack>
                        ) : (
                          <Input
                            size="sm"
                            w="100px"
                            value={value !== null && value !== undefined ? String(value) : ""}
                            onChange={(e) => {
                              const raw = e.target.value
                              if (raw === "") {
                                handleFieldChange(field.key, null)
                              } else {
                                const parsed = parseInt(raw, 10)
                                if (!isNaN(parsed)) {
                                  handleFieldChange(field.key, parsed)
                                }
                              }
                            }}
                            type="number"
                            placeholder="Score"
                            variant="flushed"
                            textAlign="right"
                          />
                        )}
                      </HStack>
                    )
                  })}
                </Stack>
              )}
            </Box>
          )
        })}
      </Stack>

      {punchlist && (
        <Text fontSize="xs" color="text.muted" mt={3}>
          Last modified: {new Date(punchlist.updatedAt).toLocaleDateString()}
        </Text>
      )}
    </Box>
  )
}
